import React from 'react';
import { CheckIcon, HeadphonesIcon, PhoneIcon, ZapIcon } from '@newcore/icons';
import { Badge } from '@newcore/ui/badge';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { BadgeColors, ButtonVariants } from '@newcore/ui/enums/variants';
import { Text } from '@newcore/ui/typography';

interface PairingIntroStateProps {
  onSetupApp: () => void;
  onSkip: () => void;
}

// Benefits list - defined outside component to avoid recreation
const BENEFITS = [
  'One-tap login saves you 15+ seconds per sign-in',
  'Biometric verification \u2013 no codes to remember',
  'Fewer password prompts throughout your day',
] as const;

/**
 * Intro state - shows "One More Thing" recommendation before pairing
 */
export const PairingIntroState: React.FC<PairingIntroStateProps> = ({
  onSetupApp,
  onSkip,
}): React.ReactElement => {
  return (
    <div className='flex flex-col items-center text-center space-y-5'>
      {/* Icon */}
      <div className='flex h-14 w-14 items-center justify-center rounded-full bg-secondary'>
        <ZapIcon className='h-7 w-7 text-primary' />
      </div>

      {/* Header */}
      <div className='space-y-1.5'>
        <Text
          as='h3'
          size={TextSizes.XL}
          weight={FontWeights.SEMIBOLD}
          className='tracking-tight'
        >
          One More Thing
        </Text>
        <Text as='p' className='text-tertiary'>
          Add the mobile app for the best experience
        </Text>
      </div>

      {/* App Recommendation Card */}
      <div className='w-full rounded-xl border bg-primary p-4 text-left'>
        {/* App Info */}
        <div className='flex items-center gap-3'>
          <div className='flex h-10 w-10 items-center justify-center rounded-lg border bg-primary'>
            <PhoneIcon className='h-5 w-5 text-tertiary' />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 flex-wrap'>
              <Text weight={FontWeights.SEMIBOLD}>Core Mobile App</Text>
              <Badge color={BadgeColors.BRAND}>Recommended</Badge>
            </div>
            <Text as='p' size={TextSizes.XS} className='text-tertiary mt-0.5'>
              Recommended for faster access
            </Text>
          </div>
        </div>

        {/* Separator */}
        <div className='my-3 border-t' />

        {/* Benefits List */}
        <div className='space-y-2.5'>
          {BENEFITS.map(benefit => (
            <div
              key={benefit}
              className='flex items-start gap-2.5 text-tertiary'
            >
              <CheckIcon className='h-4 w-4 text-tertiary mt-0.5 shrink-0' />
              <Text>{benefit}</Text>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className='w-full space-y-2 pt-1'>
        <Button onClick={onSetupApp} size={Sizes.LG} className='w-full'>
          Set up Core Mobile App
        </Button>
        <Button
          variant={ButtonVariants.TERTIARY}
          size={Sizes.LG}
          onClick={onSkip}
          className='w-full text-tertiary'
        >
          Skip for now →
        </Button>
      </div>

      {/* Help Link */}
      <Text
        as='p'
        size={TextSizes.XS}
        className='flex items-center gap-1.5 text-tertiary'
      >
        <HeadphonesIcon className='h-3.5 w-3.5' />
        Need help? Contact IT
      </Text>
    </div>
  );
};

PairingIntroState.displayName = 'PairingIntroState';
